import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEye, faTrash, faUserShield, faHandshake, faFileContract, faTruck, faUsers, faClock, faCalculator } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import { useFlashMessage } from './FlashMessage';
import TradeOpsDeleteModal from './TradeOpsDeleteModal';
import './OperationsAccessList.css';

// Cache for groupings data to persist across tab switches
let cachedGroupings = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Call this to force a fresh fetch on next mount (e.g. after create/edit)
export const invalidateGroupingsCache = () => {
  cachedGroupings = null;
  cacheTimestamp = null;
};

const OperationsAccessList = () => {
  const [groupings, setGroupings] = useState(cachedGroupings || []);
  const [loading, setLoading] = useState(!cachedGroupings);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(null);
  const dropdownRefs = useRef({});
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();

  useEffect(() => {
    // Use cache for tab switches (avoids refetch on every tab click),
    // but fetch fresh data if cache is expired or was invalidated
    const now = Date.now();
    const cacheValid = cachedGroupings && cacheTimestamp && (now - cacheTimestamp < CACHE_DURATION);
    
    if (cacheValid) {
      setGroupings(cachedGroupings);
      setLoading(false);
    } else {
      fetchGroupings();
    }
  }, []);

  useEffect(() => {
    // Click outside handler for dropdowns
    const handleClickOutside = (event) => {
      if (event.target.closest('.gear-button') || event.target.closest('.gear-icon')) {
        return;
      }
      
      const clickedDropdown = event.target.closest('.action-dropdown');
      const clickedMenu = event.target.closest('.dropdown-menu');
      
      if (!clickedDropdown && !clickedMenu && dropdownVisible !== null) {
        setDropdownVisible(null);
        document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
          el.classList.remove('dropdown-open');
          const menu = el.querySelector('.dropdown-menu');
          if (menu) {
            menu.classList.remove('dropdown-upward');
          }
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownVisible]);

  const fetchGroupings = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/operations-access/groupings/');
      setGroupings(response.data);
      // Update cache
      cachedGroupings = response.data;
      cacheTimestamp = Date.now();
    } catch (error) {
      console.error('Error fetching access groupings:', error);
      showError('Failed to load access groupings.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (groupingId, groupingName) => {
    setIsDeactivating(true);
    try {
      await axiosInstance.delete(`/api/operations-access/groupings/${groupingId}/`);
      showSuccess(`Access grouping "${groupingName}" has been deactivated.`);
      setDeleteConfirm(null);
      // Remove the deactivated grouping from state and cache
      const updatedGroupings = groupings.filter(g => g.id !== groupingId);
      setGroupings(updatedGroupings);
      cachedGroupings = updatedGroupings;
      cacheTimestamp = Date.now();
    } catch (error) {
      console.error('Error deactivating grouping:', error);
      showError('Failed to deactivate access grouping.');
    } finally {
      setIsDeactivating(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const time = date.toLocaleTimeString();
    return `${day}/${month}/${year} ${time}`;
  };

  const toggleDropdown = (id) => {
    const newActiveDropdown = dropdownVisible === id ? null : id;
    setDropdownVisible(newActiveDropdown);
    
    // Remove dropdown-open from ALL dropdowns
    document.querySelectorAll('.action-dropdown.dropdown-open').forEach(el => {
      el.classList.remove('dropdown-open');
      const menu = el.querySelector('.dropdown-menu');
      if (menu) {
        menu.classList.remove('dropdown-upward');
      }
    });
    
    // Add dropdown-open to the new active dropdown
    if (newActiveDropdown !== null) {
      const dropdownElement = dropdownRefs.current[newActiveDropdown];
      if (dropdownElement) {
        dropdownElement.classList.add('dropdown-open');
        
        const menu = dropdownElement.querySelector('.dropdown-menu');
        if (menu) {
          const rect = menu.getBoundingClientRect();
          const spaceBelow = window.innerHeight - rect.bottom;
          
          if (spaceBelow < 0) {
            menu.classList.add('dropdown-upward');
          }
        }
      }
    }
  };

  if (loading) {
    return (
      <section className="ops-access-section">
        <div className="ops-access-loading">Loading access groupings...</div>
      </section>
    );
  }

  return (
    <section className="ops-access-section">
      <div className="button-group">
        <div className="button-group-left">
          <button 
            onClick={() => history.push('/dashboard/trades/operations-access/create')}
            className="action-button"
          >
            Add Operations Access
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Main Operator</th>
              <th>Deals</th>
              <th>Recaps</th>
              <th>Deliveries</th>
              <th>Budgets</th>
              <th>Members</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupings.length > 0 ? (
              groupings.map((grouping) => {
                const totalRecaps = (grouping.trade_recap_count || 0) + (grouping.freight_recap_count || 0);
                
                return (
                  <tr 
                    key={grouping.id}
                    onClick={(e) => {
                      // Don't navigate if clicking on the action dropdown
                      if (e.target.closest('.action-dropdown')) return;
                      history.push(`/dashboard/trades/operations-access/edit/${grouping.id}`);
                    }}
                  >
                    <td className="ops-access-name-cell">{grouping.name}</td>
                    <td>{grouping.main_operator_name || '-'}</td>
                    <td className="center-cell">
                      <div className="ops-access-hover-container">
                        <div className="ops-access-count-badge deals">
                          <FontAwesomeIcon icon={faHandshake} style={{ marginRight: '4px' }} />
                          {grouping.deal_count || 0}
                        </div>
                        {(grouping.deal_numbers?.length || 0) > 0 && (
                          <div className="ops-access-details-dropdown">
                            {grouping.deal_numbers.map((number, index) => (
                              <div key={index} className="ops-access-detail-item">
                                <FontAwesomeIcon icon={faHandshake} style={{ marginRight: '6px', color: '#4a6b25' }} />
                                {number}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="center-cell">
                      <div className="ops-access-hover-container">
                        <div className="ops-access-count-badge recaps">
                          <FontAwesomeIcon icon={faFileContract} style={{ marginRight: '4px' }} />
                          {totalRecaps}
                        </div>
                        {(totalRecaps > 0) && (
                          <div className="ops-access-details-dropdown">
                            {(grouping.trade_recap_numbers || []).map((number, index) => (
                              <div key={`trade-${index}`} className="ops-access-detail-item">
                                <FontAwesomeIcon icon={faFileContract} style={{ marginRight: '6px', color: '#5e35b1' }} />
                                {number}
                              </div>
                            ))}
                            {(grouping.freight_recap_numbers || []).map((number, index) => (
                              <div key={`freight-${index}`} className="ops-access-detail-item">
                                <FontAwesomeIcon icon={faFileContract} style={{ marginRight: '6px', color: '#5e35b1' }} />
                                {number}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="center-cell">
                      <div className="ops-access-hover-container">
                        <div className="ops-access-count-badge deliveries">
                          <FontAwesomeIcon icon={faTruck} style={{ marginRight: '4px' }} />
                          {grouping.delivery_count || 0}
                        </div>
                        {(grouping.delivery_numbers?.length || 0) > 0 && (
                          <div className="ops-access-details-dropdown">
                            {grouping.delivery_numbers.map((number, index) => (
                              <div key={index} className="ops-access-detail-item">
                                <FontAwesomeIcon icon={faTruck} style={{ marginRight: '6px', color: '#3d5a1f' }} />
                                {number}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="center-cell">
                      <div className="ops-access-hover-container">
                        <div className="ops-access-count-badge budgets">
                          <FontAwesomeIcon icon={faCalculator} style={{ marginRight: '4px' }} />
                          {grouping.budget_count || 0}
                        </div>
                        {(grouping.budget_names?.length || 0) > 0 && (
                          <div className="ops-access-details-dropdown">
                            {grouping.budget_names.map((name, index) => (
                              <div key={index} className="ops-access-detail-item">
                                <FontAwesomeIcon icon={faCalculator} style={{ marginRight: '6px', color: '#2e7d32' }} />
                                {name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="center-cell">
                      <span className="ops-access-count-badge members">
                        <FontAwesomeIcon icon={faUsers} style={{ marginRight: '4px' }} />
                        {grouping.member_count || 0}
                      </span>
                    </td>
                    <td>
                      <div className="created-at-badge">
                        <FontAwesomeIcon icon={faClock} className="created-at-icon" />
                        <span className="created-at-text">
                          {formatDateTime(grouping.created_at)}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="action-dropdown" ref={el => dropdownRefs.current[grouping.id] = el}>
                        <button className="gear-button" onClick={(e) => {
                          e.stopPropagation();
                          toggleDropdown(grouping.id);
                        }}>
                          Actions
                        </button>
                        {dropdownVisible === grouping.id && (
                          <div className="dropdown-menu">
                            <button onClick={(e) => {
                              e.stopPropagation();
                              history.push(`/dashboard/trades/operations-access/edit/${grouping.id}`);
                            }}>
                              <FontAwesomeIcon icon={faEye} style={{ marginRight: '8px' }} />
                              View / Edit
                            </button>
                            <hr />
                            <button 
                              className="danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(grouping);
                                setDropdownVisible(null);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                              Deactivate
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="empty-state-cell">
                  <div className="empty-state-container">
                    <div className="empty-state-icon">
                      <FontAwesomeIcon icon={faUserShield} />
                    </div>
                    <div className="empty-state-text">
                      <h3>No Access Groupings Yet</h3>
                      <p>Click "Add Operations Access" to create your first access grouping</p>
                    </div>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Deactivation Confirmation */}
      <TradeOpsDeleteModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => handleDeactivate(deleteConfirm.id, deleteConfirm.name)}
        itemType="grouping"
        itemDetails={deleteConfirm ? {
          groupingName: deleteConfirm.name,
          mainOperator: deleteConfirm.main_operator_name || '-',
          memberCount: deleteConfirm.member_count || 0,
        } : null}
        isDeleting={isDeactivating}
      />
    </section>
  );
};

export default OperationsAccessList;
